import { notesAfterDelete } from '@/lib/noteThreads';
import ProfileFlagControl from '@/components/admin/ProfileFlagControl';
import { PersonFieldsProvider, PersonFieldsToolbar, PersonFieldsStandalone } from '@/components/admin/PersonFields';
import { agencyOption, selectAgency } from '@/lib/personAgency';
import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { normalizeAddressCountryCode, regionCode } from '@/lib/addressRegions';
import { toDateInputValue } from '@/lib/dates';
import {
  applicationNoteAuthorLabel,
  applicationNoteEditorLabel,
} from '@/lib/auditIdentity';
import {
  getApplication,
  updateApplication,
  retryApplicationDocumentDrafts,
  deleteApplication,
  addApplicationNote,
  updateApplicationNote,
  deleteApplicationNote,
  addStudentNote,
  updateStudentNote,
  deleteStudentNote,
  convertApplication,
  revertApplicationEnrollment,
  getApplicationDocumentUrl,
  uploadApplicationDocument,
  getAgents,
  getPrograms,
  getStudentsMeta,
  revealApplicationSin,
  lockApplication,
  unlockApplication,
} from '@/lib/api';
import {
  GENDER_OPTIONS,
  STATUS_IN_CANADA_OPTIONS,
  CAMPUS_OPTIONS,
  ATTENDANCE_OPTIONS,
  PATHWAY_OPTIONS,
  DOC_TYPE_OPTIONS,
  statusMeta,
  statusOptionsFor,
  labelFromOptions,
  badgeClass,
  formatDate,
  formatDateTime,
} from '@/lib/applications';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EditableField as DField } from '@/components/admin/ProfileField';
import {
  applicationToPerson,
  studentToPerson,
  mergeStudentOntoApplication,
} from '@/lib/personView';
import ProfileHero from '@/components/profile/ProfileHero';
import {
  ProfileLayoutToolbar,
  ProfileSection,
  ProfileSectionGrid,
  useProfileLayout,
} from '@/components/admin/ProfileLayout';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import PersonalSection from '@/components/admin/person/PersonalSection';
import StudentSections from '@/components/admin/person/StudentSections';
import AgencySection from '@/components/admin/person/AgencySection';
import ReferralSection from '@/components/admin/person/ReferralSection';
import ProgramSection from '@/components/admin/person/ProgramSection';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import OfficialDocumentsCard from '@/components/admin/OfficialDocumentsCard';
import OfferPackEmailCard from '@/components/admin/OfferPackEmailCard';
import LanguageTestsCard from '@/components/admin/LanguageTestsCard';
import FrenchProgramSection from '@/components/admin/person/FrenchProgramSection';
import { hasFrenchAnswers } from '@/lib/frenchIntake';
import NotesThread from '@/components/admin/NotesThread';
import PortalEmailComposer from '@/components/admin/PortalEmailComposer';
import { useAuth } from '@/contexts/AuthContext';
import {
  ArrowLeft, PencilLine, Save, X, User, Building2, GraduationCap,
  FileCheck2, FileText, ShieldCheck, ShieldAlert,
  UserPlus, UserCheck, Copy, ExternalLink, AlertTriangle, Download, Loader2,
  RotateCcw, Trash2, Lock, Unlock, Mail, Upload,
} from 'lucide-react';
import AgencyCombobox from '@/components/admin/AgencyCombobox';

const DOC_TYPE_LABELS = Object.fromEntries(DOC_TYPE_OPTIONS.map((o) => [o.value, o.label]));
const APPLICATION_DOCUMENT_DOC_TYPES = [
  { value: 'offer_letter', label: 'Offer Letter' },
  { value: 'enrolment_contract', label: 'Student Enrolment Contract' },
  { value: 'fee_payment_instructions', label: 'Fee Payment Instructions' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'receipt', label: 'Receipt' },
];
const FSL_PROGRAM_NAME = 'French as a Second Language (FSL)';

function formatFileSize(bytes) {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const inputClass =
  'border-[rgba(6, 43, 26,0.16)] focus-visible:ring-[3px] focus-visible:ring-[rgba(212, 175, 55,0.35)] focus-visible:ring-offset-0 focus-visible:border-[#D4AF37]';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ASAP_START_VALUE = 'asap';
const STARTING_MONTH_COUNT = 24;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const NONE_VALUE = '__none__';

/* The shared spine first, then what only an applicant has. One controller over
   both means a single Customize button edits the whole page while the spine
   keeps the arrangement the student page shows. */
const APPLICATION_PAGE_SURFACES = ['person_profile', 'student_profile', 'application_profile'];

/* Bridge to the shared person sections.

   The page goes on editing the application's own field names -- the save
   handler, the validation and the status workflow all speak them -- and these
   translate at the render boundary, so the shared sections never learn them.
   Keeping the translation here rather than rewriting the form state is what
   makes adopting the shared cards a change of markup rather than a change of
   behaviour. */
const PERSON_TO_APPLICATION_FIELD = {
  firstName: 'first_name',
  lastName: 'last_name',
  preferredName: 'preferred_name',
  email: 'email',
  phoneCountryCode: 'phone_country_code',
  phoneNumber: 'phone_number',
  dateOfBirth: 'date_of_birth',
  gender: 'gender',
  citizenshipCountry: 'country_of_citizenship',
  statusInCanada: 'status_in_canada',
  statusExpiryDate: 'status_expiry_date',
  agentId: 'agent_id',
  agentFirstName: 'agent_first_name',
  agentLastName: 'agent_last_name',
  agencyCompanyName: 'agency_company_name',
  agentPhone: 'agent_phone',
  agentEmail: 'agent_email',
  agencyNotes: 'agency_notes',
  heardAboutUs: 'heard_about_us',
  heardAboutUsDetail: 'heard_about_us_detail',
  frenchGoal: 'french_goal',
  frenchTargetLevel: 'french_target_level',
  frenchCurrentLevel: 'french_current_level',
  frenchTestProof: 'french_test_proof',
  frenchPlacementPreference: 'french_placement_preference',
  program: 'program',
  selectedAddOns: 'selected_add_ons',
  intendedStart: 'intended_semester',
  campus: 'campus',
  attendanceMode: 'attendance_mode',
  universityPathway: 'university_pathway',
};

const PERSON_TO_APPLICATION_ADDRESS = {
  unit: 'addr_unit',
  street: 'addr_street',
  city: 'addr_city',
  provinceState: 'addr_province_state',
  country: 'addr_country',
  postalCode: 'addr_postal_code',
};

/* The edit form flattens the nested address and stores the agency flag as a
   string, so reading it back into the shared shape is not quite
   `applicationToPerson`. */
function applicationFormToPerson(form) {
  if (!form) return null;
  return applicationToPerson({
    ...form,
    using_agency: form.using_agency === 'true' || form.using_agency === true,
    address: {
      unit: form.addr_unit,
      street: form.addr_street,
      city: form.addr_city,
      province_state: form.addr_province_state,
      country: form.addr_country,
      postal_code: form.addr_postal_code,
    },
  });
}

function safeList(value) {
  return Array.isArray(value) ? value : [];
}

function programKey(value) {
  return String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function programSelectOptions(programs, currentProgram) {
  const names = [...new Set(safeList(programs)
    .map((program) => String(program?.name || '').trim())
    .filter(Boolean))]
    .sort((a, b) => a.localeCompare(b));
  const current = String(currentProgram || '').trim();
  if (current && !names.includes(current)) {
    names.push(current);
  }
  return names.map((name) => ({
    value: name,
    label: name === current && !safeList(programs).some((program) => program?.name === name)
      ? `${name} (legacy)`
      : name,
  }));
}

function selectedProgramRecord(programs, programName) {
  const key = programKey(programName);
  return safeList(programs).find((program) => programKey(program?.name) === key);
}

function programAddOns(program) {
  return safeList(program?.add_ons).filter((item) => item?.id && item?.label);
}

function selectedProgramEndDate(program, selectedAddOns, start) {
  const selected = new Set(safeList(selectedAddOns));
  const extraEnds = programAddOns(program)
    .filter((item) => selected.has(item.id))
    .flatMap((item) => safeList(item.end_dates))
    .filter((row) => row?.start === start && row?.end)
    .map((row) => row.end);
  if (extraEnds.length) return extraEnds.sort().at(-1);
  const intake = safeList(program?.intakes).find((row) => row?.start === start);
  return intake?.end || '';
}

function programStartingDateOptions(program, currentValue) {
  const options = [{ value: ASAP_START_VALUE, label: 'As soon as possible' }];
  const seen = new Set(options.map((option) => option.value));
  const addOption = (value, endValue = '') => {
    const text = String(value || '').trim();
    if (!text || seen.has(text)) return;
    seen.add(text);
    const endText = String(endValue || '').trim();
    options.push({
      value: text,
      label: endText
        ? `${intendedStartingDateDisplay(text)} - ${intendedStartingDateDisplay(endText)}`
        : intendedStartingDateDisplay(text),
    });
  };

  safeList(program?.intakes).forEach((item) => {
    if (typeof item === 'string') {
      addOption(item);
    } else if (item && typeof item === 'object') {
      addOption(item.start, item.end);
    }
  });
  safeList(program?.semesters).forEach((item) => addOption(item));

  if (options.length === 1) {
    return buildStartingDateOptions(currentValue);
  }
  if (currentValue && !seen.has(currentValue)) {
    options.push({
      value: currentValue,
      label: `${intendedStartingDateDisplay(currentValue)} (legacy)`,
    });
  }
  return options;
}

function requiresEnrolmentContract(record, programKey = 'program') {
  if (typeof record?.program_requires_enrolment_contract === 'boolean') {
    return record.program_requires_enrolment_contract;
  }
  return record?.[programKey] === FSL_PROGRAM_NAME;
}

function documentTypesForContractRequirement(docTypes, enabled) {
  return enabled ? docTypes : docTypes.filter((docType) => docType.value !== 'enrolment_contract');
}

function apiErrorMessage(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') {
    return detail.message || fallback;
  }
  return fallback;
}

function formDate(value) {
  return toDateInputValue(value);
}

function monthOptionValue(date) {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${year}-${month}-01`;
}

function monthOptionLabel(value) {
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function intendedStartingDateDisplay(value) {
  if (!value) return '—';
  if (value === ASAP_START_VALUE) return 'As soon as possible';
  if (ISO_DATE_RE.test(value)) return formatDate(value);
  return value;
}

function buildStartingDateOptions(currentValue) {
  const now = new Date();
  const startYear = now.getFullYear();
  const startMonth = now.getMonth();
  const monthOptions = Array.from({ length: STARTING_MONTH_COUNT }, (_, index) => {
    const date = new Date(Date.UTC(startYear, startMonth + index, 1));
    const value = monthOptionValue(date);
    return { value, label: monthOptionLabel(value) };
  });
  const options = [
    { value: ASAP_START_VALUE, label: 'As soon as possible' },
    ...monthOptions,
  ];
  if (currentValue && !options.some((option) => option.value === currentValue)) {
    options.push({
      value: currentValue,
      label: `${intendedStartingDateDisplay(currentValue)} (legacy)`,
      disabled: true,
    });
  }
  return options;
}

function applicantInitials(app) {
  const initials = [app?.first_name, app?.last_name]
    .map((part) => String(part || '').trim().charAt(0))
    .filter(Boolean)
    .join('')
    .toUpperCase();
  return initials || '?';
}

function agentDisplayName(agent) {
  return agent?.company_name || agent?.preferredName || agent?.agent_name || agent?.id || '';
}

/* The agent contact details an agency record can supply for an application.
   Applications carry their own copies of these fields because an agency is
   optional -- a direct applicant can name an agent who is not in the CRM. When
   an agency IS linked, there is no reason to retype what the agency already
   knows, so these become the fallback and the autofill source.

   Agency notes are deliberately excluded: the application's agency notes are
   about this applicant, while the agency's own notes are internal CRM remarks
   that should not be copied onto a student record. */
function agentDetailsFromAgency(agent) {
  if (!agent) return null;
  const contact = agent.mainContact && typeof agent.mainContact === 'object' ? agent.mainContact : {};
  const fullName = String(contact.name || agent.agent_name || '').trim();
  const nameParts = fullName.split(/\s+/).filter(Boolean);
  return {
    agent_first_name: nameParts.length ? nameParts[0] : '',
    // Everything after the first token, so "Mary Anne Smith" keeps "Anne Smith".
    agent_last_name: nameParts.length > 1 ? nameParts.slice(1).join(' ') : '',
    agency_company_name: String(agent.company_name || '').trim(),
    agent_phone: String(contact.phone || agent.phone || agent.contactInfo || '').trim(),
    agent_email: String(contact.email || agent.email || '').trim(),
  };
}

function submittedDateValue(app) {
  return Object.prototype.hasOwnProperty.call(app || {}, 'submitted_at')
    ? app.submitted_at
    : app?.created_at;
}

/* Build the editable form from an application record. */
function makeForm(a) {
  const addr = a.address || {};
  return {
    custom_fields: { ...(a.custom_fields || {}), ...(a.student_mirror?.custom_fields || {}) },
    status: a.status || 'applied',
    application_number: String(a.application_number || a.converted_student_number || ''),
    enrollment_status: a.converted_student_id ? a.student_mirror?.enrollment_status ?? a.enrollment_status ?? '' : '',
    first_name: a.first_name || '',
    last_name: a.last_name || '',
    preferred_name: a.preferred_name || '',
    date_of_birth: formDate(a.date_of_birth),
    gender: a.gender || '',
    country_of_citizenship: a.country_of_citizenship || '',
    phone_country_code: a.phone_country_code || '',
    phone_number: a.phone_number || '',
    email: a.email || '',
    status_in_canada: a.status_in_canada || '',
    status_expiry_date: formDate(a.status_expiry_date),
    submitted_at: formDate(submittedDateValue(a)),
    converted_at: formDate(a.converted_at),
    addr_unit: addr.unit || '',
    addr_street: addr.street || '',
    addr_city: addr.city || '',
    addr_province_state: addr.province_state || '',
    addr_postal_code: addr.postal_code || '',
    addr_country: addr.country || '',
    using_agency: (a.using_agency || a.agent_id) ? 'true' : 'false',
    agent_id: a.agent_id || '',
    agent_first_name: a.agent_first_name || '',
    agent_last_name: a.agent_last_name || '',
    agency_company_name: a.agency_company_name || '',
    agent_phone: a.agent_phone || '',
    agent_email: a.agent_email || '',
    agency_notes: a.agency_notes || '',
    heard_about_us: a.heard_about_us || '',
    heard_about_us_detail: a.heard_about_us_detail || '',
    french_goal: a.french_goal || '',
    french_target_level: a.french_target_level || '',
    french_current_level: a.french_current_level || '',
    french_test_proof: a.french_test_proof || '',
    french_placement_preference: a.french_placement_preference || '',
    program: a.program || '',
    intended_semester: a.intended_semester || '',
    campus: a.campus || '',
    attendance_mode: a.attendance_mode === 'hybrid' ? 'blended' : a.attendance_mode === 'online' ? 'online_live' : (a.attendance_mode || ''),
    university_pathway: a.university_pathway || '',
    selected_add_ons: safeList(a.selected_add_ons),
  };
}

/* Compute the minimal PATCH payload: only fields that changed vs the record.
   Address is sent as a whole object if any of its subfields changed. Mirrors
   the backend invariant that agent fields are empty when using_agency=false. */
function buildPatch(form, original) {
  const patch = {};
  const usingAgency = form.using_agency === 'true';

  const next = {
    application_number: form.application_number.trim().toUpperCase(),
    status: form.status,
    first_name: form.first_name.trim(),
    last_name: form.last_name.trim(),
    preferred_name: form.preferred_name.trim(),
    date_of_birth: form.date_of_birth,
    gender: form.gender,
    country_of_citizenship: form.country_of_citizenship.trim(),
    phone_country_code: form.phone_country_code.trim(),
    phone_number: form.phone_number.trim(),
    email: form.email.trim(),
    status_in_canada: form.status_in_canada,
    status_expiry_date: form.status_expiry_date,
    submitted_at: form.submitted_at,
    converted_at: form.converted_at,
    using_agency: usingAgency,
    agent_id: usingAgency ? form.agent_id : '',
    agent_first_name: usingAgency ? form.agent_first_name.trim() : '',
    agent_last_name: usingAgency ? form.agent_last_name.trim() : '',
    agency_company_name: usingAgency ? form.agency_company_name.trim() : '',
    agent_phone: usingAgency ? form.agent_phone.trim() : '',
    agent_email: usingAgency ? form.agent_email.trim() : '',
    agency_notes: form.agency_notes.trim(),
    // An agency answers this question; the server enforces the same rule, so a
    // stale form value can never leave the two disagreeing.
    heard_about_us: usingAgency ? 'agency' : form.heard_about_us || '',
    heard_about_us_detail: form.heard_about_us_detail || '',
    // Sent as answered; the server re-derives the tree and clears whatever the
    // answers above no longer ask for (french_intake.normalize_answers).
    french_goal: form.french_goal || '',
    french_target_level: form.french_target_level || '',
    french_current_level: form.french_current_level || '',
    french_test_proof: form.french_test_proof || '',
    french_placement_preference: form.french_placement_preference || '',
    program: form.program,
    intended_semester: form.intended_semester,
    campus: form.campus,
    attendance_mode: form.attendance_mode,
    university_pathway: form.university_pathway,
    selected_add_ons: safeList(form.selected_add_ons),
  };

  Object.keys(next).forEach((key) => {
    const originalValue = key === 'submitted_at'
      ? submittedDateValue(original)
      : original[key];
    const orig = originalValue ?? (typeof next[key] === 'boolean' ? false : '');
    if ((key === 'submitted_at' || key === 'converted_at') && next[key] !== formDate(orig)) {
      patch[key] = next[key];
      return;
    }
    if (Array.isArray(next[key])) {
      if (JSON.stringify(next[key]) !== JSON.stringify(Array.isArray(orig) ? orig : [])) patch[key] = next[key];
    } else if (next[key] !== orig) patch[key] = next[key];
  });

  // Address diff (whole-object replacement).
  const origAddr = original.address || {};
  const nextAddr = {
    unit: form.addr_unit.trim(),
    street: form.addr_street.trim(),
    city: form.addr_city.trim(),
    province_state: regionCode(form.addr_country, form.addr_province_state),
    postal_code: form.addr_postal_code.trim(),
    country: form.addr_country.trim(),
  };
  const addrChanged = Object.keys(nextAddr).some(
    (k) => nextAddr[k] !== (origAddr[k] || ''),
  );
  if (addrChanged) patch.address = nextAddr;

  const originalEnrollment = original.student_mirror?.enrollment_status ?? original.enrollment_status ?? '';
  if (original.converted_student_id && !original.student_profile_deleted && form.enrollment_status !== originalEnrollment) patch.enrollment_status = form.enrollment_status;
  const originalCustom = { ...(original.custom_fields || {}), ...(original.student_mirror?.custom_fields || {}) };
  const changedCustom = Object.fromEntries(Object.entries(form.custom_fields || {}).filter(([key, value]) => value !== originalCustom[key]));
  if (Object.keys(changedCustom).length) patch.custom_fields = changedCustom;
  return { patch, usingAgency, next };
}

/* A label/value pair that swaps to an edit control while editing. */
export default function AdminApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [retryingDocuments, setRetryingDocuments] = useState(false);
  const [documentRevision, setDocumentRevision] = useState(0);
  const [agents, setAgents] = useState([]);
  const [programRecords, setProgramRecords] = useState([]);
  const [addressMeta, setAddressMeta] = useState({ countries: [] });

  const [revealedSin, setRevealedSin] = useState('');
  const [revealingSin, setRevealingSin] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deletingApp, setDeletingApp] = useState(false);
  const layoutController = useProfileLayout(APPLICATION_PAGE_SURFACES);
  const [confirmingLock, setConfirmingLock] = useState(false);
  const [lockPending, setLockPending] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const [deleteNote, setDeleteNote] = useState(null);
  const [deletingNoteId, setDeletingNoteId] = useState(null);
  const [noteActionError, setNoteActionError] = useState('');

  const [convertOpen, setConvertOpen] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertResult, setConvertResult] = useState(null); // { student_id, student_number, user_id, temp_password }
  const [revertOpen, setRevertOpen] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [revertError, setRevertError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setNotFound(false);
        const res = await getApplication(id);
        if (!cancelled) setApp(res.data);
      } catch (err) {
        if (cancelled) return;
        console.error(err);
        if (err?.response?.status === 404) {
          setNotFound(true);
        } else {
          toast.error(err?.response?.data?.detail || 'Could not load application');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getAgents().catch((err) => {
        console.error(err);
        return { data: [] };
      }),
      getPrograms().catch((err) => {
        console.error(err);
        return { data: [] };
      }),
      getStudentsMeta().catch((err) => {
        console.error(err);
        return { data: { countries: [] } };
      }),
    ])
      .then(([agentsRes, programsRes, metaRes]) => {
        if (cancelled) return;
        setAgents(safeList(agentsRes.data));
        setProgramRecords(safeList(programsRes.data));
        setAddressMeta(metaRes.data || { countries: [] });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const setText = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const setField = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

  /* Picking an agency fills the agent fields from that agency's record. Only
     blanks are filled -- anything already typed is left alone, so this can
     never quietly overwrite a correction someone made by hand. */
  const selectLinkedAgency = (agentId) => {
    setForm((prev) => selectAgency(prev, agentId, agents));
  };
  const setAddressCountry = (value) => setForm((prev) => ({
    ...prev,
    addr_country: value,
    addr_province_state: normalizeAddressCountryCode(prev.addr_country) === normalizeAddressCountryCode(value)
      ? prev.addr_province_state
      : '',
  }));

  /* What the shared sections are handed. Once an applicant is converted the
     admissions page shows the student's current answers, because that is what
     the record means until someone freezes it; a locked record shows the copy
     taken at that moment instead. The merge is what keeps a field the student
     record cannot express -- self-paced against live -- from being flattened
     on the way through. */
  const hasStudentMirror = Boolean(
    app?.student_mirror && Object.keys(app.student_mirror).length,
  );
  const lockState = app?.lock || {};
  const isLocked = lockState.locked === true;
  const personView = useMemo(() => {
    const base = applicationToPerson(app);
    const mirror = app?.student_mirror;
    if (!mirror || !Object.keys(mirror).length) return base;
    return mergeStudentOntoApplication(base, studentToPerson(mirror));
  }, [app]);
  const personForm = useMemo(() => applicationFormToPerson(form), [form]);

  // The shared sections report edits under the shared names; the form still
  // holds the application's own.
  const setPersonField = (key, value) => {
    if (key === 'usingAgency') {
      setField('using_agency')(value ? 'true' : 'false');
      return;
    }
    const target = PERSON_TO_APPLICATION_FIELD[key];
    if (target) setField(target)(value);
  };
  const setPersonAddressField = (key, value) => {
    if (key === 'country') {
      setAddressCountry(value);
      return;
    }
    const target = PERSON_TO_APPLICATION_ADDRESS[key];
    if (target) setField(target)(value);
  };
  const programOptions = useMemo(
    () => programSelectOptions(programRecords, form?.program || app?.program),
    [app?.program, form?.program, programRecords],
  );
  const selectedProgram = useMemo(
    () => selectedProgramRecord(programRecords, form?.program || app?.program),
    [app?.program, form?.program, programRecords],
  );
  const availableAddOns = useMemo(() => programAddOns(selectedProgram), [selectedProgram]);
  /* The French card is shown for a programme that asks those questions, and for
     any record that already carries an answer — a programme changed after the
     fact does not unsay what the applicant told us. */
  const showFrenchSection = Boolean(selectedProgram?.french_questions_enabled)
    || hasFrenchAnswers(personView) || hasFrenchAnswers(personForm);
  const hasFrenchResultDocument = safeList(app?.documents)
    .some((document) => document?.doc_type === 'french_results');
  const changeProgram = (program) => {
    const nextProgram = selectedProgramRecord(programRecords, program);
    const allowed = new Set(programAddOns(nextProgram).map((item) => item.id));
    setForm((prev) => ({
      ...prev,
      program,
      selected_add_ons: safeList(prev?.selected_add_ons).filter((item) => allowed.has(item)),
    }));
  };
  const startingDateOptions = useMemo(
    () => programStartingDateOptions(selectedProgram, form?.intended_semester),
    [form?.intended_semester, selectedProgram],
  );
  const displayedEndDate = selectedProgramEndDate(
    selectedProgram,
    editing ? form?.selected_add_ons : app?.selected_add_ons,
    editing ? form?.intended_semester : app?.intended_semester,
  ) || app?.intake_end_date;
  const applicationDocumentDocTypes = useMemo(
    () => documentTypesForContractRequirement(
      APPLICATION_DOCUMENT_DOC_TYPES,
      requiresEnrolmentContract(app),
    ),
    [app],
  );

  const startEditing = () => {
    const base = makeForm(app);
    // An application linked to an agency but missing agent details shows the
    // agency's values read-only; seed the form with them too, so opening the
    // editor does not present blanks and saving keeps what was on screen.
    const agency = safeList(agents).find((item) => item.id === String(app.agent_id || '').trim());
    const details = agentDetailsFromAgency(agency);
    if (details) {
      Object.entries(details).forEach(([key, value]) => {
        if (value && !String(base[key] || '').trim()) base[key] = value;
      });
    }
    setForm(base);
    setEditing(true);
  };
  const cancelEditing = () => {
    setEditing(false);
    setForm(null);
  };

  const handleRetryDocuments = async () => {
    setRetryingDocuments(true);
    try {
      const res = await retryApplicationDocumentDrafts(id);
      setApp(res.data);
      setDocumentRevision((value) => value + 1);
      toast.success('Admission documents are ready');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not prepare admission documents');
    } finally {
      setRetryingDocuments(false);
    }
  };

  const handleSave = async () => {
    if (!form) return;
    if (!form.application_number.trim() && (app.application_number || app.converted_student_number)) {
      toast.error('Applicant / Student ID is required');
      return;
    }
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error('First and last name are required');
      return;
    }
    if (!EMAIL_RE.test(form.email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }
    const { patch, usingAgency } = buildPatch(form, app);
    if (usingAgency) {
      const hasLinkedAgency = !!form.agent_id;
      const required = ['agent_first_name', 'agent_last_name', 'agency_company_name', 'agent_phone', 'agent_email'];
      if (!hasLinkedAgency && required.some((k) => !form[k].trim())) {
        toast.error('Select an agency or enter the agent name, company, phone and email');
        return;
      }
      if (form.agent_email.trim() && !EMAIL_RE.test(form.agent_email.trim())) {
        toast.error('Please enter a valid agent email address');
        return;
      }
    }
    if (Object.keys(patch).length === 0) {
      toast.info('No changes to save');
      cancelEditing();
      return;
    }
    try {
      setSaving(true);
      const res = await updateApplication(id, patch);
      setApp(res.data);
      toast.success('Application updated');
      if (res.data.document_drafts_pending) {
        toast.info('Admission documents are pending. Use Retry admission documents to prepare them.');
      }
      cancelEditing();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async (rawBody, parentId, parentNote) => {
    const body = String(rawBody || '').trim();
    if (!body) return;
    try {
      setAddingNote(true);
      setNoteActionError('');
      const ownerType = parentNote?.ownerType || 'application';
      const ownerId = parentNote?.ownerId || id;
      const res = ownerType === 'student'
        ? await addStudentNote(ownerId, {
          text: body,
          parent_id: parentId || null,
          source_type: 'admissions',
          source_id: id,
        })
        : await addApplicationNote(ownerId, {
          body,
          parent_id: parentId || null,
          source_type: 'admissions',
          source_id: id,
        });
      setApp((prev) => ({ ...prev, notes: [...(prev.notes || []), res.data] }));
      toast.success('Note added');
    } catch (err) {
      console.error(err);
      const msg = apiErrorMessage(err, 'Could not add note');
      setNoteActionError(msg);
      toast.error(msg);
    } finally {
      setAddingNote(false);
    }
  };

  const handleSaveNote = async (note, rawBody) => {
    const body = String(rawBody || '').trim();
    if (!body) {
      toast.error('Note body is required');
      return;
    }
    try {
      setNoteActionError('');
      const ownerType = note.ownerType || 'application';
      const ownerId = note.ownerId || id;
      const res = ownerType === 'student'
        ? await updateStudentNote(ownerId, note.id, { text: body })
        : await updateApplicationNote(ownerId, note.id, { body });
      setApp((prev) => ({
        ...prev,
        notes: safeList(prev?.notes).map((item) => (
          item.id === note.id ? res.data : item
        )),
      }));
      toast.success('Note updated');
    } catch (err) {
      console.error(err);
      const msg = apiErrorMessage(err, 'Could not update note');
      setNoteActionError(msg);
      toast.error(msg);
    }
  };

  const handleThreadDeleteNote = async (note) => {
    if (!note?.id) return;
    setNoteActionError('');
    setDeleteNote({ ...note, body: note.text });
  };

  const openDeleteNote = (note) => {
    setNoteActionError('');
    setDeleteNote(note);
  };

  const closeDeleteNote = () => {
    if (deletingNoteId) return;
    setDeleteNote(null);
  };

  const handleDeleteNote = async () => {
    if (!deleteNote?.id) return;
    try {
      setDeletingNoteId(deleteNote.id);
      setNoteActionError('');
      const ownerType = deleteNote.ownerType || 'application';
      const ownerId = deleteNote.ownerId || id;
      if (ownerType === 'student') await deleteStudentNote(ownerId, deleteNote.id);
      else await deleteApplicationNote(ownerId, deleteNote.id);
      setApp((prev) => ({
        ...prev,
        notes: notesAfterDelete(prev?.notes, deleteNote.id),
      }));
      setDeleteNote(null);
      toast.success('Note deleted');
    } catch (err) {
      console.error(err);
      const msg = apiErrorMessage(err, 'Could not delete note');
      setNoteActionError(msg);
      toast.error(msg);
    } finally {
      setDeletingNoteId(null);
    }
  };

  const handleConvert = async () => {
    try {
      setConverting(true);
      const res = await convertApplication(id);
      setConvertResult(res.data);
      setApp((prev) => ({
        ...prev,
        status: 'enrolled',
        converted_student_id: res.data.student_id,
        converted_student_number: res.data.student_number,
        converted_at: res.data.converted_at || prev.converted_at,
        student_profile_deleted: false,
        deleted_student_id: '',
        student_profile_deleted_at: '',
      }));
      toast.success('Student account created');
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : detail?.message || 'Could not convert application';
      // If the server says it's already converted, reflect the link in the UI.
      if (detail && typeof detail === 'object' && detail.student_id) {
        setApp((prev) => ({
          ...prev,
          status: 'enrolled',
          converted_student_id: detail.student_id,
          converted_student_number: detail.student_number,
        }));
      }
      setConvertOpen(false);
      toast.error(msg);
    } finally {
      setConverting(false);
    }
  };

  const closeConvertDialog = () => {
    setConvertOpen(false);
    setConvertResult(null);
  };

  const openRevertDialog = () => {
    setRevertError('');
    setRevertOpen(true);
  };

  const closeRevertDialog = () => {
    if (reverting) return;
    setRevertOpen(false);
    setRevertError('');
  };

  const handleRevertEnrollment = async () => {
    try {
      setReverting(true);
      setRevertError('');
      const res = await revertApplicationEnrollment(id);
      const nextApplication = res.data?.application;
      setApp((prev) => nextApplication || {
        ...prev,
        status: 'offered',
        converted_student_id: null,
        converted_at: null,
      });
      setConvertResult(null);
      setRevertOpen(false);
      toast.success('Enrollment reverted');
    } catch (err) {
      console.error(err);
      const msg = apiErrorMessage(err, 'Could not revert enrollment');
      setRevertError(msg);
      toast.error(msg);
    } finally {
      setReverting(false);
    }
  };

  const copyTempPassword = async () => {
    if (!convertResult?.temp_password) return;
    try {
      await navigator.clipboard.writeText(convertResult.temp_password);
      toast.success('Temp password copied');
    } catch {
      toast.error('Copy failed — select and copy manually');
    }
  };

  // Index of the document currently being fetched (for a per-row spinner).
  const [docLoadingIndex, setDocLoadingIndex] = useState(null);

  const handleViewDocument = async (index) => {
    try {
      setDocLoadingIndex(index);
      const res = await getApplicationDocumentUrl(id, index);
      // Short-lived presigned URL — open it immediately in a new tab.
      window.open(res.data.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not open document');
    } finally {
      setDocLoadingIndex(null);
    }
  };

  // Staff upload onto the application. `index` null adds a document; a number
  // replaces the document at that position.
  const [docUpload, setDocUpload] = useState(null);
  const [docUploading, setDocUploading] = useState(false);

  const openDocUpload = (index = null) => {
    const current = index === null ? null : (app?.documents || [])[index];
    setDocUpload({ index, docType: current?.doc_type || DOC_TYPE_OPTIONS[0].value, file: null });
  };

  const handleDocUpload = async () => {
    const file = docUpload?.file;
    if (!file) return;
    if (!['application/pdf', 'image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Only PDF, JPG and PNG files are accepted');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error('The file must be smaller than 15 MB');
      return;
    }
    try {
      setDocUploading(true);
      const res = await uploadApplicationDocument(id, file, { docType: docUpload.docType, replaceIndex: docUpload.index });
      setApp(res.data);
      toast.success(docUpload.index === null ? 'Document added' : 'Document replaced');
      setDocUpload(null);
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : err?.message || 'Could not upload the document');
    } finally {
      setDocUploading(false);
    }
  };

  const handleConfirmedDelete = async () => {
    try {
      setDeletingApp(true);
      await deleteApplication(id);
      toast.success('Application deleted — SIN and uploaded documents removed');
      navigate('/admin/applications');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not delete the application');
      setDeletingApp(false);
    }
  };

  /* Locking changes what the whole page shows -- the student sections stop
     tracking and the lock state drives every control -- so both handlers
     refetch rather than patching a field in place. */
  const reloadApplication = async () => {
    const res = await getApplication(id);
    setApp(res.data);
  };

  const handleLock = async () => {
    try {
      setLockPending(true);
      await lockApplication(id);
      await reloadApplication();
      setConfirmingLock(false);
      toast.success('Admissions record locked');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not lock the record');
    } finally {
      setLockPending(false);
    }
  };

  const handleUnlock = async () => {
    try {
      setLockPending(true);
      await unlockApplication(id);
      await reloadApplication();
      setConfirmingLock(false);
      toast.success('Admissions record unlocked');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not unlock the record');
    } finally {
      setLockPending(false);
    }
  };

  const handleRevealSin = async () => {
    try {
      setRevealingSin(true);
      const res = await revealApplicationSin(id);
      setRevealedSin(res.data?.sin || '');
      toast.success('SIN revealed');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not reveal SIN');
    } finally {
      setRevealingSin(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 animate-fade-in">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-40 bg-[#F7F8FA] animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  if (notFound || !app) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/admin/applications')} className="text-[#062B1A] -ml-2">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to admissions
        </Button>
        <p className="text-[#062B1A]/60">Application not found.</p>
      </div>
    );
  }

  const meta = statusMeta(app.status);
  const statusChoices = statusOptionsFor(app.status);
  // A locked record accepts no status change and no conversion -- the server
  // refuses both -- so the controls are not offered rather than offered and
  // then rejected.
  const canChangeStatus = statusChoices.length > 1 && !isLocked;
  const isConverted = !!app.converted_student_id;
  const studentProfileDeleted = Boolean(app.student_profile_deleted) && !app.converted_student_id;
  const canConvert = app.status === 'offered' && !app.converted_student_id && !isLocked;
  const noteHistory = safeList(app.notes).map((note) => ({
    ...note,
    text: note.body ?? note.text ?? '',
    author: applicationNoteAuthorLabel(note),
    editedBy: applicationNoteEditorLabel(note),
    authorId: note.author_user_id || note.author_id || note.authorId || '',
    parentId: note.parent_id || note.parentId || null,
    mentions: note.mentions || [],
    createdAt: note.created_at || note.createdAt,
    lastActivityAt: note.last_activity_at || note.lastActivityAt || note.created_at || note.createdAt,
    editedAt: note.edited_at || note.editedAt,
    sourceLabel: note.source_label || note.sourceLabel || '',
    sourceType: note.source_type || note.sourceType || '',
    sourceId: note.source_id || note.sourceId || '',
    sourceHref: note.source_href || note.sourceHref || '',
    ownerType: note.owner_type || note.ownerType || 'application',
    ownerId: note.owner_id || note.ownerId || id,
  }));
  const documents = app.documents || [];
  const submittedAt = submittedDateValue(app);
  const linkedAgentId = String((editing ? form?.agent_id : personView.agentId) || '').trim();
  const linkedAgent = agents.find((agent) => agent.id === linkedAgentId);
  const linkedAgentName = linkedAgent ? agentDisplayName(linkedAgent) : linkedAgentId;
  const linkedAgencyLegalName = linkedAgent?.company_name || (editing ? form?.agency_company_name : personView.agencyCompanyName);
  /* Applications saved before an agency was linked have blank agent fields, so
     read through to the agency record rather than showing a row of dashes.
     Named under the shared field names, since that is what the section speaks. */
  const agencyFallback = agentDetailsFromAgency(linkedAgent);
  const agencyFallbackPerson = agencyFallback && {
    agentFirstName: agencyFallback.agent_first_name,
    agentLastName: agencyFallback.agent_last_name,
    agencyCompanyName: agencyFallback.agency_company_name,
    agentPhone: agencyFallback.agent_phone,
    agentEmail: agencyFallback.agent_email,
  };
  // A brand or preferred name would otherwise be lost now the separate
  // "Agency" row is gone from the read-only view.
  const linkedAgencyPreferredName = String(linkedAgent?.preferredName || '').trim();
  // The combobox does its own filtering, so the full list goes to it.
  const agentOptions = safeList(agents)
    .map(agencyOption)
    .filter((option) => option.id);
  if (editing && form?.agent_id && !agentOptions.some((option) => option.id === form.agent_id)) {
    const selected = safeList(agents).find((agent) => agent.id === form.agent_id);
    if (selected) agentOptions.unshift(agencyOption(selected));
  }

  return (
    <PersonFieldsProvider values={editing ? form?.custom_fields : { ...(app.custom_fields || {}), ...(app.student_mirror?.custom_fields || {}) }} editing={editing} onChange={(key, value) => setForm((prev) => ({ ...prev, custom_fields: { ...prev.custom_fields, [key]: value } }))}>
    <div className="space-y-6 animate-fade-in">
      {/* Top bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="ghost" onClick={() => navigate('/admin/applications')} className="text-[#062B1A] -ml-2">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to admissions
        </Button>
        {!editing ? (
          <div className="flex flex-wrap items-center gap-2">
            <PortalEmailComposer
              audience="applications"
              recipients={[app]}
              buttonLabel="Email applicant"
              buttonClassName="border-[#D4AF37]/45 text-[#7A5A05]"
            />
            {/* Only the super admin draws the line between "still being
                worked on" and "this is what they told us". The server enforces
                that too -- hiding the button is a courtesy, not the check. */}
            {currentUser?.is_super_admin && (
              <Button
                variant="outline"
                onClick={() => setConfirmingLock(true)}
                disabled={lockPending}
                className="border-[#D4AF37]/45 text-[#7A5A05]"
              >
                {isLocked ? (
                  <><Unlock className="w-4 h-4 mr-2" /> Unlock record</>
                ) : (
                  <><Lock className="w-4 h-4 mr-2" /> Lock record</>
                )}
              </Button>
            )}
            {!isLocked && (
              <Button
                variant="outline"
                onClick={() => setConfirmingDelete(true)}
                className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </Button>
            )}
            <ProfileLayoutToolbar controller={layoutController} />
            <PersonFieldsToolbar disabled={editing || layoutController.editMode} />
            {!isLocked && !layoutController.editMode && (
              <Button onClick={startEditing} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
                <PencilLine className="w-4 h-4 mr-2" /> Edit details
              </Button>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={cancelEditing} disabled={saving} className="border-[rgba(6, 43, 26,0.16)] text-[#062B1A]">
              <X className="w-4 h-4 mr-2" /> Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        )}
      </div>

      {/* A locked record says so before anything else on the page, because
          every control below it behaves differently. */}
      {isLocked && (
        <div className="flex items-start gap-3 rounded-lg border border-[#D4AF37]/45 bg-[rgba(212,175,55,0.08)] p-4">
          <Lock className="mt-0.5 h-4 w-4 shrink-0 text-[#7A5A05]" />
          <div className="text-sm text-[#062B1A]">
            <p className="font-semibold">This admissions record is locked.</p>
            <p className="text-[#062B1A]/70">
              It is the account of what the applicant told us
              {lockState.locked_by_name ? `, locked by ${lockState.locked_by_name}` : ''}
              {lockState.locked_at ? ` on ${formatDate(lockState.locked_at)}` : ''}
              . Changes to the student profile no longer reach it. Notes can still be added.
              {app.converted_student_id && !app.student_profile_deleted && " Payments and billing show the linked student's current account."}
            </p>
          </div>
        </div>
      )}

      <Dialog open={confirmingLock} onOpenChange={(open) => !lockPending && setConfirmingLock(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isLocked ? 'Unlock this admissions record?' : 'Lock this admissions record?'}</DialogTitle>
            <DialogDescription>
              {isLocked
                ? 'The record will start tracking the student profile again, and admissions staff will be able to edit it. The copy frozen at the lock is discarded. This is recorded in the notes.'
                : 'The record stops changing: it keeps the values it has now, corrections to the student profile stop reaching it, and nobody can edit it until a super admin unlocks it. Notes can still be added. This is recorded in the notes.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmingLock(false)} disabled={lockPending}>
              Cancel
            </Button>
            <Button
              onClick={isLocked ? handleUnlock : handleLock}
              disabled={lockPending}
              className="bg-[#062B1A] text-white hover:bg-[#041B10]"
            >
              {isLocked ? <Unlock className="w-4 h-4 mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
              {lockPending ? 'Working…' : (isLocked ? 'Unlock record' : 'Lock record')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmingDelete} onOpenChange={(open) => !deletingApp && setConfirmingDelete(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this application?</DialogTitle>
            <DialogDescription>
              {isConverted
                ? 'This application is linked to a converted student. Delete the student record (or revert the enrollment) first, then delete the application.'
                : `This permanently removes ${[app.first_name, app.last_name].filter(Boolean).join(' ') || 'the applicant'}'s application, including the SIN, all uploaded documents and any prepared offer documents. This cannot be undone.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmingDelete(false)} disabled={deletingApp}>
              Cancel
            </Button>
            {!isConverted && (
              <Button
                onClick={handleConfirmedDelete}
                disabled={deletingApp}
                className="bg-red-600 text-white hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" /> {deletingApp ? 'Deleting…' : 'Delete application'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* The same header the student page shows, so an applicant and the
          student they become are recognisably one person. */}
      <ProfileHero
        avatar={(
          <Avatar className="h-24 w-24 border-4 border-white shadow-md">
            <AvatarFallback className="bg-[#D4AF37] text-2xl font-bold text-[#062B1A]">
              {applicantInitials(app)}
            </AvatarFallback>
          </Avatar>
        )}
      >
        <h2 className="break-words text-2xl font-semibold text-white" style={{ fontFamily: 'var(--font-display)' }}>
          {`${app.first_name || ''} ${app.last_name || ''}`.trim() || 'Applicant'}
        </h2>
        <div className="flex items-center gap-2 break-all text-sm text-white/90">
          <Mail className="h-4 w-4 shrink-0" /> {app.email || 'No email'}
        </div>
        {editing ? (
          <div className="max-w-xs space-y-1">
            <Label htmlFor="admissions-person-number" className="text-xs text-white/90">Applicant / Student ID</Label>
            <Input
              id="admissions-person-number"
              value={form.application_number}
              onChange={(event) => setForm((prev) => ({ ...prev, application_number: event.target.value.toUpperCase() }))}
              maxLength={40}
              className="border-white/40 bg-white text-[#062B1A]"
            />
            <p className="text-xs text-white/80">This number is shared with the linked student profile.</p>
          </div>
        ) : (
          <div className="text-sm text-white/90">
            Applicant ID: {app.application_number || app.converted_student_number || 'Not assigned'}
          </div>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          <Badge variant="outline" className="border-white/40 bg-white font-semibold text-[#062B1A]">
            Applicant
          </Badge>
          {editing ? (
            <Select value={form.status} onValueChange={setField('status')} disabled={!canChangeStatus}>
              <SelectTrigger aria-label="Applicant status" className="h-7 w-auto min-w-[120px] gap-2 rounded-full border-white/40 bg-white/[0.15] px-3 text-xs font-semibold text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusChoices.map((status) => <SelectItem key={status} value={status}>{statusMeta(status).label}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Badge variant="outline" className="border-white/40 bg-white/[0.15] text-white">
              {meta.label}
            </Badge>
          )}
          {isLocked && (
            <Badge variant="outline" className="border-white/40 bg-white/[0.15] text-white">
              <Lock className="mr-1 h-3 w-3" /> Locked
            </Badge>
          )}
          <ProfileFlagControl key={app.id || id} kind="application" recordId={app.id || id} />
        </div>
      </ProfileHero>

      <ProfileSectionGrid>
          {/* Personal, Agency and Program are the shared spine: the student
              page renders these same three components from the same view
              model, so the two pages cannot drift apart again. */}
          <ProfileSection id="personal" controller={layoutController}>
          <PersonalSection
            title={layoutController.sectionById.personal?.title}
            person={personView}
            form={personForm}
            editing={editing}
            setField={setPersonField}
            setAddressField={setPersonAddressField}
            countries={safeList(addressMeta.countries)}
            sinMask={app.sinMask}
            revealedSin={revealedSin}
            onRevealSin={handleRevealSin}
            revealingSin={revealingSin}
            title="Personal"
          />
          </ProfileSection>

          <ProfileSection id="agency" controller={layoutController}>
          <AgencySection
            title={layoutController.sectionById.agency?.title}
            person={personView}
            form={personForm}
            editing={editing}
            setField={setPersonField}
            agentOptions={agentOptions}
            onSelectAgency={selectLinkedAgency}
            linkedAgencyName={linkedAgentName}
            linkedAgencyLegalName={linkedAgencyLegalName}
            linkedAgencyPreferredName={linkedAgencyPreferredName}
            agentFallback={agencyFallbackPerson}
            linkedAgencyHref={linkedAgentId ? `/admin/agencies/${linkedAgentId}` : ''}
          />
          </ProfileSection>

        <ProfileSection id="referral" controller={layoutController}>
          <ReferralSection
            title={layoutController.sectionById.referral?.title}
            person={personView}
            form={personForm}
            editing={editing}
            setField={setPersonField}
          />
        </ProfileSection>

          {showFrenchSection && (
            <ProfileSection id="french_program" controller={layoutController}>
              <FrenchProgramSection
                title={layoutController.sectionById.french_program?.title}
                person={personView}
                form={personForm}
                editing={editing}
                setField={setPersonField}
                hasTestResultDocument={hasFrenchResultDocument}
              />
            </ProfileSection>
          )}

          <ProfileSection id="language_tests" controller={layoutController}>
            <LanguageTestsCard targetType="application" targetId={app.id} />
          </ProfileSection>

          <ProfileSection id="program" controller={layoutController}>
          <ProgramSection
            enrollmentStatus={app.converted_student_id ? app.student_mirror?.enrollment_status ?? app.enrollment_status : ''}
            enrollmentStatusDraft={form?.enrollment_status}
            enrollmentStatusFallback={app.converted_student_id ? 'Not set' : 'Not enrolled'}
            enrollmentStatusOptions={[...new Set([...(addressMeta.enrollment_status || []), app.student_mirror?.enrollment_status, app.enrollment_status].filter(Boolean))].map((value) => ({ value, label: value }))}
            onEnrollmentStatusChange={app.converted_student_id && !app.student_profile_deleted && !isLocked ? setField('enrollment_status') : undefined}
            title={layoutController.sectionById.program?.title}
            person={personView}
            form={personForm}
            editing={editing}
            setField={setPersonField}
            onChangeProgram={changeProgram}
            programOptions={programOptions}
            startingDateOptions={startingDateOptions}
            availableAddOns={availableAddOns}
            formatStart={intendedStartingDateDisplay}
            endDate={displayedEndDate}
            showEnglishProficiency={false}
            showApplicationFee={false}
          />
          </ProfileSection>
          {/* The student sections, read-only. Live values while the record
              still tracks the student, the copy frozen at the lock once it
              stops -- the server decides which, and hands over one shape. */}
          {hasStudentMirror && (
            <StudentSections
              student={{ ...app.student_mirror, id: app.converted_student_id }}
              editing={false}
              layoutController={layoutController}
              courses={safeList(app.student_mirror?.courses)}
              showLiveFinance={Boolean(app.converted_student_id) && !app.student_profile_deleted}
              financeReadOnly={isLocked}
              onTuitionPaymentsChanged={reloadApplication}
            />
          )}

          {!hasStudentMirror && <PersonFieldsStandalone ids={['compliance', 'loan', 'deposit', 'tuition', 'billing', 'courses']} controller={layoutController} />}

          {/* Declaration & Documents */}
          <ProfileSection id="declaration" controller={layoutController}>
          <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-[#062B1A] flex items-center gap-2" style={{ fontFamily: 'var(--font-display)' }}>
                <FileCheck2 className="w-4 h-4 text-[#D4AF37]" /> Declaration &amp; Documents
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <DField label="Signature (typed legal name)" editing={false} display={app.signature_full_name} />
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#062B1A]/45">
                    Policies agreement
                  </Label>
                  <div className="flex items-center gap-2 text-sm">
                    {app.agreed_to_policies ? (
                      <>
                        <ShieldCheck className="w-4 h-4 text-[rgb(21,128,61)]" />
                        <span className="text-[#062B1A] font-medium">Agreed</span>
                      </>
                    ) : (
                      <>
                        <ShieldAlert className="w-4 h-4 text-[rgb(153,27,27)]" />
                        <span className="text-[#062B1A] font-medium">Not agreed</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Uploaded supporting documents. Files live in private R2 storage;
                  each View fetches a short-lived presigned URL (audit-logged). */}
              {documents.length === 0 ? (
                <div className="rounded-lg border border-dashed border-[rgba(6, 43, 26,0.18)] bg-[#F7F8FA] p-5 text-center">
                  <FileText className="w-6 h-6 mx-auto mb-2 text-[#062B1A]/35" />
                  <p className="text-sm font-medium text-[#062B1A]/70">No documents uploaded</p>
                  <p className="text-xs text-[#062B1A]/45 mt-1">
                    The applicant did not attach any supporting files with this application.
                  </p>
                </div>
              ) : (
                <ul className="space-y-2">
                  {documents.map((d, index) => (
                    <li
                      key={d.key || index}
                      className="flex items-center gap-3 rounded-lg border border-[rgba(6, 43, 26,0.1)] bg-white p-3"
                    >
                      <FileText className="w-5 h-5 text-[#D4AF37] shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-[#062B1A] truncate">
                          {DOC_TYPE_LABELS[d.doc_type] || d.doc_type}
                        </p>
                        <p className="text-xs text-[#062B1A]/50 truncate">
                          {d.filename}
                          {formatFileSize(d.size) ? ` · ${formatFileSize(d.size)}` : ''}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewDocument(index)}
                        disabled={docLoadingIndex === index}
                        className="border-[rgba(6, 43, 26,0.16)] text-[#062B1A] shrink-0"
                      >
                        {docLoadingIndex === index ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        View
                      </Button>
                      {!isLocked && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openDocUpload(index)}
                          className="border-[rgba(6, 43, 26,0.16)] text-[#062B1A] shrink-0"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Replace
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {!isLocked && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openDocUpload(null)}
                  className="border-[rgba(6, 43, 26,0.16)] text-[#062B1A]"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Add document
                </Button>
              )}

              <Dialog open={Boolean(docUpload)} onOpenChange={(open) => { if (!open && !docUploading) setDocUpload(null); }}>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>{docUpload?.index === null ? 'Add a document' : 'Replace document'}</DialogTitle>
                    <DialogDescription>
                      {docUpload?.index === null
                        ? 'Attach a supporting document to this application.'
                        : "The new file takes this document's place. The current file is removed from storage."}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      <Label>Document type</Label>
                      <Select
                        value={docUpload?.docType || ''}
                        onValueChange={(value) => setDocUpload((current) => ({ ...current, docType: value }))}
                        disabled={docUpload?.index !== null}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[
                            ...DOC_TYPE_OPTIONS,
                            ...(docUpload?.docType && !DOC_TYPE_LABELS[docUpload.docType]
                              ? [{ value: docUpload.docType, label: docUpload.docType }]
                              : []),
                          ].map((option) => (
                            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="application-document-file">File</Label>
                      <Input
                        id="application-document-file"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                        onChange={(event) => {
                          const file = event.target.files?.[0] || null;
                          setDocUpload((current) => ({ ...current, file }));
                        }}
                      />
                      <p className="text-xs text-[#062B1A]/50">PDF, JPG or PNG, up to 15 MB.</p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setDocUpload(null)} disabled={docUploading}>Cancel</Button>
                    <Button
                      onClick={handleDocUpload}
                      disabled={docUploading || !docUpload?.file}
                      className="bg-[#062B1A] text-white hover:bg-[#041B10]"
                    >
                      {docUploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      {docUpload?.index === null ? 'Add document' : 'Replace file'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>

          </ProfileSection>

          <ProfileSection id="official_documents" controller={layoutController}>
          {(app.status !== 'offered' || isConverted) && (
            <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-[#062B1A]">Admission Documents</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-[#062B1A]/70">
                {isConverted ? (
                  <>
                    <p>Admission documents moved to the student profile when this applicant was converted.</p>
                    <Button variant="outline" onClick={() => navigate(`/admin/students/${app.converted_student_id}`)}>
                      Open student documents
                    </Button>
                  </>
                ) : (
                  <p>Save the application status as Offered to prepare the offer letter, invoice and other admission drafts here. If document preparation fails, the status is still saved and a retry button appears here.</p>
                )}
              </CardContent>
            </Card>
          )}
          {app.status === 'offered' && !isConverted && (
            <>
              {app.document_drafts_pending && (
                <div role="status" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  <p>The application was saved. Some admission documents could not be prepared. Retry when the document service is available.</p>
                  <Button variant="outline" className="mt-2" onClick={handleRetryDocuments} disabled={retryingDocuments || isLocked || editing}>
                    {retryingDocuments ? 'Preparing documents...' : 'Retry admission documents'}
                  </Button>
                </div>
              )}
              <OfferPackEmailCard
                key={`email-${documentRevision}`}
                application={app}
                onApplicationUpdated={(nextApplication) => setApp(nextApplication)}
              />
              <OfficialDocumentsCard
                key={`documents-${documentRevision}`}
                ownerType="application"
                ownerId={app.id || id}
                docTypes={applicationDocumentDocTypes}
                title="Admission Documents"
                description="Use Generate draft to create a missing document, Edit to update a prepared document, or Download to save its PDF."
              />
            </>
          )}
          </ProfileSection>
          {/* Status control */}
          <ProfileSection id="application_status" controller={layoutController}>
          <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                Application status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={`text-sm px-3 py-1 ${badgeClass(meta.tone)}`}>
                  {meta.label}
                </Badge>
              </div>

              {studentProfileDeleted && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  <div className="flex items-center gap-2 font-medium">
                    <AlertTriangle className="w-4 h-4" />
                    Student profile deleted
                  </div>
                  <p className="mt-1 text-xs text-red-700/80">
                    The linked student profile was deleted. You can convert this admission to a student again or delete this admission record.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 gap-3">
                <DField label="Submitted" editing={editing} display={formatDate(submittedAt)}>
                  <Input type="date" value={form?.submitted_at || ''} onChange={setText('submitted_at')} className={inputClass} />
                </DField>
                <DField label="Converted to student" editing={editing} display={formatDate(app.converted_at)}>
                  <Input type="date" value={form?.converted_at || ''} onChange={setText('converted_at')} className={inputClass} />
                </DField>
              </div>

              {/* Convert-to-student: only from offered, before conversion. */}
              {canConvert && (
                <Button
                  onClick={() => { setConvertResult(null); setConvertOpen(true); }}
                  className="w-full bg-[#D4AF37] text-[#062B1A] hover:bg-[#c69a37]"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Convert to student
                </Button>
              )}

              {/* Converted indicator + link to the student record. */}
              {isConverted && app.converted_student_id && (
                <div className="rounded-lg border border-[rgba(34,197,94,0.25)] bg-[rgba(34,197,94,0.08)] p-3 space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium text-[rgb(21,128,61)]">
                    <UserCheck className="w-4 h-4" />
                    Converted — student account active
                  </div>
                  <p className="text-[11px] text-[#062B1A]/55 break-all">
                    Student ID: {app.converted_student_number || 'Not assigned'}
                  </p>
                  <p className="text-[11px] text-[#062B1A]/55">
                    Converted: {formatDate(app.converted_at)}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/admin/students/${app.converted_student_id}`)}
                    className="w-full border-[rgba(6, 43, 26,0.16)] text-[#062B1A]"
                  >
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                    Open student record
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={openRevertDialog}
                    disabled={reverting}
                    className="w-full"
                  >
                    {reverting ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    Revert enrollment
                  </Button>
                </div>
              )}

            </CardContent>
          </Card>

          </ProfileSection>

          <ProfileSection id="notes" controller={layoutController}>
          <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                Notes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <NotesThread
                isSuperAdmin={currentUser?.role === 'admin' && currentUser?.is_super_admin === true}
                notes={noteHistory}
                currentUserId={currentUser?.id || ''}
                onAdd={handleAddNote}
                onEdit={handleSaveNote}
                onDelete={handleThreadDeleteNote}
                deletingId={deletingNoteId}
                emptyText="No notes yet. Add the first."
              />

              {noteActionError && (
                <div className="rounded-lg border border-[rgba(153,27,27,0.22)] bg-[rgba(254,242,242,0.9)] p-3 text-sm text-[rgb(153,27,27)]">
                  {noteActionError}
                </div>
              )}

            </CardContent>
          </Card>
        </ProfileSection>
      </ProfileSectionGrid>

      {/* Convert-to-student dialog: confirm, then show the one-time temp password. */}
      <Dialog
        open={convertOpen}
        onOpenChange={(open) => {
          if (!open && !converting) closeConvertDialog();
        }}
      >
        <DialogContent className="sm:max-w-md">
          {!convertResult ? (
            <>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'var(--font-display)' }}>
                  Convert to student
                </DialogTitle>
                <DialogDescription>
                  This creates a login account for{' '}
                  <span className="font-medium text-[#062B1A]">
                    {`${app.first_name || ''} ${app.last_name || ''}`.trim() || app.email}
                  </span>{' '}
                  and a linked student record, and marks this application as enrolled. Continue?
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={closeConvertDialog} disabled={converting}>
                  Cancel
                </Button>
                <Button
                  onClick={handleConvert}
                  disabled={converting}
                  className="bg-[#062B1A] text-white hover:bg-[#041B10]"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  {converting ? 'Converting…' : 'Create student account'}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'var(--font-display)' }}>
                  Student account created
                </DialogTitle>
                <DialogDescription>
                  Share this temporary password securely with the applicant. They should change
                  it on first login. <span className="font-medium">It won&apos;t be shown again.</span>
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-3">
                <div className="rounded-lg border border-[rgba(212, 175, 55,0.4)] bg-[rgba(212, 175, 55,0.08)] p-3 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-[#9A6D06] mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-[#9A6D06]">
                    This is the only time this password is displayed. Copy it now.
                  </p>
                </div>
                <div>
                  <Label className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#062B1A]/45">
                    Student ID
                  </Label>
                  <Input
                    readOnly
                    value={convertResult.student_number || 'Not assigned'}
                    className={`${inputClass} mt-1.5 font-mono`}
                  />
                </div>
                <div>
                  <Label className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#062B1A]/45">
                    Temporary password
                  </Label>
                  <div className="flex gap-2 mt-1.5">
                    <Input
                      readOnly
                      value={convertResult.temp_password}
                      onFocus={(e) => e.target.select()}
                      className={`${inputClass} font-mono`}
                    />
                    <Button
                      variant="outline"
                      onClick={copyTempPassword}
                      className="border-[rgba(6, 43, 26,0.16)] text-[#062B1A] flex-shrink-0"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-[#062B1A]/55">
                  Login email: <span className="font-medium text-[#062B1A]">{app.email}</span>
                </p>
              </div>

              <DialogFooter>
                <Button onClick={closeConvertDialog} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
                  Done
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteNote}
        onOpenChange={(open) => {
          if (!open) closeDeleteNote();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
              Delete note
            </AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes this note from the application.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteNote?.body && (
            <div className="rounded-lg border border-[rgba(6,43,26,0.12)] bg-[#F7F8FA] p-3 text-sm text-[#062B1A] whitespace-pre-wrap break-words max-h-32 overflow-auto">
              {deleteNote.body}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!deletingNoteId}>Cancel</AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteNote}
              disabled={!!deletingNoteId}
            >
              {deletingNoteId ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              {deletingNoteId ? 'Deleting...' : 'Delete note'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={revertOpen}
        onOpenChange={(open) => {
          if (open) {
            setRevertOpen(true);
          } else {
            closeRevertDialog();
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
              Revert enrollment
            </AlertDialogTitle>
            <AlertDialogDescription>
              This reverts the application to Offered and permanently deletes the linked student record. This can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {revertError && (
            <div className="rounded-lg border border-[rgba(153,27,27,0.22)] bg-[rgba(254,242,242,0.9)] p-3 text-sm text-[rgb(153,27,27)]">
              {revertError}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reverting}>Cancel</AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              onClick={handleRevertEnrollment}
              disabled={reverting}
            >
              {reverting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RotateCcw className="w-4 h-4 mr-2" />
              )}
              {reverting ? 'Reverting...' : 'Revert enrollment'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </PersonFieldsProvider>
  );
}
